import { FormEvent, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Form, Formik, FormikHelpers } from "formik";
import { useReducedMotionConfig } from "framer-motion";
import { navigate } from "gatsby";
import { useTranslation } from "gatsby-plugin-react-i18next";

import { CountryCode } from "@settings/countries";
import { PetSpecies } from "@settings/pet";
import { Events, track } from "@utils/analytics";
import { rollbar } from "@utils/rollbar";

import Button from "@atoms/button/Button";
import RadioFieldset, { RadioFieldSetVariantsName } from "@molecules/radio-fieldset/RadioFieldset";
import SectionTitle from "@molecules/section-title/SectionTitle";

import { useSetLead } from "@features/leads-funnel/application/lead-use-cases";
import { Lead } from "@features/leads-funnel/domain/lead";
import CatSvg from "@images/organisms/pet-species-selector/cat.svg";
import DogSvg from "@images/organisms/pet-species-selector/dog.svg";

import * as styles from "@organisms/pet-species-selector/PetSpeciesSelector.module.scss";

interface PetPetSpeciesSelectorProps {
  countryCodeIso: CountryCode;
  adoptionClassName?: string;
}

interface PetSpeciesFormValues {
  petSpecies: PetSpecies | null;
}

const PetSpeciesSelector = ({
  countryCodeIso = CountryCode.ES,
  adoptionClassName,
}: PetPetSpeciesSelectorProps): JSX.Element => {
  const { t } = useTranslation();
  const [speciesSelected, setSpeciesSelected] = useState<boolean>(false);
  const [shouldScroll, setShouldScroll] = useState<boolean>(false);
  const setLead = useSetLead();
  const reduceMotion = useReducedMotionConfig();
  const petSpeciesSelectorRef = useRef<HTMLDivElement>(null);
  const radioFieldsetOptions = [
    {
      image: <DogSvg />,
      value: PetSpecies.dog,
      title: t("common.dog"),
    },
    {
      image: <CatSvg />,
      value: PetSpecies.cat,
      title: t("common.cat"),
    },
  ];

  useEffect(() => {
    if (shouldScroll && petSpeciesSelectorRef.current) {
      petSpeciesSelectorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setShouldScroll(false);
    }
  }, [shouldScroll]);

  return (
    <section
      className={cn(styles.petSpeciesSelector, adoptionClassName, {
        [styles["hasTransition"]]: !reduceMotion,
        [styles["speciesSelected"]]: speciesSelected,
      })}
      ref={petSpeciesSelectorRef}
    >
      <SectionTitle title={t("species_selector.title")} adoptionClassName={styles.sectionTitle} />
      <Formik
        initialValues={{
          petSpecies: null,
        }}
        onSubmit={(
          values: PetSpeciesFormValues,
          { setSubmitting }: FormikHelpers<PetSpeciesFormValues>
        ): void => {
          setSubmitting(true);

          if (!values.petSpecies || !values.petSpecies.length) {
            track(Events.FORM_ANSWERED, {
              eventSender: "Pet Species Hero CTA",
              answer: Events.MISSING_ANSWER,
            });

            void navigate(`/${countryCodeIso.toLowerCase()}/onboarding/species/`);

            return;
          }

          setLead(values)
            .then((updatedLead: Lead | undefined): void => {
              if (!updatedLead) {
                setSubmitting(false);

                return;
              }

              track(Events.FORM_ANSWERED, {
                eventSender: "Pet Species Hero CTA",
                answer: values.petSpecies,
              });

              void navigate(`/${countryCodeIso.toLowerCase()}/onboarding/sex/`);
            })
            .catch(() => {
              rollbar.warn("There was an error setting the lead.");
              setSubmitting(false);
            });
        }}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form className={styles.form}>
            <RadioFieldset
              name={"petSpecies"}
              options={radioFieldsetOptions}
              legend={t("species_selector.title")}
              initialValue={null}
              variantName={RadioFieldSetVariantsName.petSpeciesSelector}
              extraEventProperties={{ question: "Pet Species on Hero" }}
              onChange={(e: FormEvent<HTMLFormElement>) => {
                void setFieldValue("petSpecies", e);
                setSpeciesSelected(true);
                setShouldScroll(true);
              }}
            />

            <Button
              type="submit"
              disabled={isSubmitting}
              isLoading={isSubmitting}
              adoptionClassName={styles.button}
            >
              {t("common.cta.see_price")}
            </Button>
          </Form>
        )}
      </Formik>
    </section>
  );
};

export default PetSpeciesSelector;
